import React from 'react';
import { cn } from 'lib/utils';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Rocket } from 'lucide-react';
import { FeatureRecommendation, getTopRecommendations } from 'utils/onboardingRecommendations';
import { getPathForPage } from 'utils/navigation';

interface OnboardingResultsProps {
  recommendations: FeatureRecommendation[];
  onGetStarted: () => void;
}

export const OnboardingResults: React.FC<OnboardingResultsProps> = ({
  recommendations,
  onGetStarted
}) => {
  const navigate = useNavigate();
  const topRecommendations = getTopRecommendations(recommendations, 5);

  const handleFeatureClick = (feature: FeatureRecommendation) => {
    let path = getPathForPage(feature.page);

    if (feature.subPage) {
      path = `${path}?tab=${feature.subPage}`;
    }

    navigate(path);
    onGetStarted();
  };

  return (
    <div className="flex flex-col gap-6 max-md:gap-5">
      <div className="flex flex-col gap-3">
        <h2 className="text-xl font-bold text-foreground-strong m-0 flex items-center gap-3 leading-tight">
          <Sparkles size={22} />
          Perfect! Here's your personalized path
        </h2>
        <p className="text-sm text-foreground-muted m-0 leading-relaxed max-[480px]:text-sm">
          Based on your goals, these features will help you most. Click any to get started immediately.
        </p>
      </div>

      <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto pr-2 -mr-2 scrollbar-thin scrollbar-thumb-slate-200 max-md:max-h-[350px] max-md:gap-2.5">
        {topRecommendations.map((feature, index) => (
          <button
 key={feature.id}
 type="button"
 className={cn(
 'flex items-center gap-3.5 p-4 rounded-xl border cursor-pointer transition-all text-left w-full hover:translate-x-[3px] hover:border-primary/35 hover:shadow-lg active:translate-x-px max-md:p-3.5 max-md:gap-3 max-[480px]:p-3 max-[480px]:gap-2.5',
 feature.priority >= 4
                ? 'border-primary/25 bg-primary/5'
                : 'border-slate-500/12 bg-card'
            )}
            onClick={() => handleFeatureClick(feature)}
          >
            <div
 className={cn(
 'flex items-center justify-center w-11 h-11 rounded-xl shrink-0 max-[480px]:w-10 max-[480px]:h-10',
 feature.priority >= 4
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-muted text-foreground-muted'
              )}
            >
              {feature.icon}
            </div>
            <div className="flex-1 flex flex-col gap-1 min-w-0">
              <span className="text-sm font-semibold text-foreground-strong flex items-center gap-2 flex-wrap leading-tight max-[480px]:text-sm">
                {feature.title}
                {feature.priority >= 4 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/[0.15] text-primary text-xs font-bold uppercase tracking-wide whitespace-nowrap">
                    <Sparkles size={9} />
                    Top Pick
                  </span>
                )}
              </span>
              <span className="text-sm text-foreground-muted leading-snug line-clamp-2 max-[480px]:text-xs">
                {feature.description}
              </span>
            </div>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-background-soft0/8 text-primary shrink-0 transition-all group-hover:bg-primary group-hover:text-white max-[480px]:w-7 max-[480px]:h-7">
              <ArrowRight size={14} />
            </div>
          </button>
        ))}
      </div>

      <div className="flex flex-col items-center gap-4 p-6 bg-primary/5 rounded-xl border-primary/12 mt-2 max-md:p-5 max-md:gap-3.5 max-[480px]:p-4 max-[480px]:gap-3">
        <p className="text-sm text-foreground-muted text-center m-0 leading-normal max-[480px]:text-sm">
          You can always explore all features from the sidebar. Ready to begin your health journey?
        </p>
        <button
 type="button"
 className="flex items-center gap-2 px-8 py-3.5 rounded-xl border-none bg-primary text-white font-semibold text-sm cursor-pointer transition-all shadow-lg hover:shadow-lg active:translate-y-0 whitespace-nowrap max-[480px]:py-3.5 max-[480px]:px-6 max-[480px]:text-sm max-[480px]:w-full max-[480px]:justify-center"
 onClick={onGetStarted}
 >
          <Rocket size={16} />
          Start Exploring
        </button>
      </div>
    </div>
  );
};
